import { MATURITY_OPTIONS } from '../../config/brsrConfig'
import type { BrsrQuestion, QuestionOption } from './types'

const maturityChoices: QuestionOption[] = MATURITY_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}))

const yesNoUnsure: QuestionOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'not_sure', label: 'Not sure' },
]

function maturity(
  partial: Omit<BrsrQuestion, 'kind' | 'options' | 'indicatorClass' | 'weight' | 'dimension'> & {
    indicatorClass?: BrsrQuestion['indicatorClass']
    weight?: number
    dimension?: BrsrQuestion['dimension']
  },
): BrsrQuestion {
  return {
    ...partial,
    kind: 'maturity',
    options: maturityChoices,
    indicatorClass: partial.indicatorClass ?? 'essential',
    weight: partial.weight ?? 1,
    required: partial.required ?? true,
    dimension: partial.dimension ?? (partial.stage === 'disclosure' ? 'disclosure' : undefined),
  }
}

const listed: BrsrQuestion['visibility'] = { all: [{ questionId: 'listing_status', equals: 'listed' }] }

const coreVisibility: BrsrQuestion['visibility'] = {
  any: [
    { questionId: 'listing_status', equals: 'listed' },
    { questionId: 'listing_status', equals: 'not_sure' },
  ],
  none: [{ questionId: 'cohort', equals: 'outside_top_1000' }],
}

const valueChainVisibility: BrsrQuestion['visibility'] = {
  any: [
    { questionId: 'supplies_listed', equals: ['yes', 'not_sure'] },
    { questionId: 'customers_request_esg', equals: ['yes', 'not_sure'] },
    { questionId: 'cohort', equals: ['top_150', 'top_151_250', 'not_sure'] },
  ],
}

export const BRSR_QUESTIONS: BrsrQuestion[] = [
  {
    id: 'company_name',
    stage: 'profile',
    section: 'Organisation',
    kind: 'text',
    label: 'Company name (optional)',
    helpText: 'Used only on your on-screen results and PDF. Leave blank if you prefer not to name the company.',
    required: false,
    indicatorClass: 'profile',
  },
  {
    id: 'sector',
    stage: 'profile',
    section: 'Organisation',
    kind: 'single',
    label: 'Industry or sector',
    required: true,
    indicatorClass: 'profile',
    options: [
      { value: 'manufacturing', label: 'Manufacturing' },
      { value: 'metals_mining_cement', label: 'Metals, mining, cement or materials' },
      { value: 'energy_utilities', label: 'Energy and utilities' },
      { value: 'chemicals_pharma', label: 'Chemicals or pharmaceuticals' },
      { value: 'fmcg_textiles', label: 'FMCG, textiles or consumer goods' },
      { value: 'automotive', label: 'Automotive and components' },
      { value: 'infrastructure', label: 'Infrastructure, construction or real estate' },
      { value: 'financial_services', label: 'Financial services' },
      { value: 'it_ites', label: 'IT, ITeS or technology services' },
      { value: 'logistics', label: 'Logistics and transport' },
      { value: 'healthcare', label: 'Healthcare' },
      { value: 'agriculture', label: 'Agriculture or food' },
      { value: 'services', label: 'Other services' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    id: 'listing_status',
    stage: 'profile',
    section: 'Listing',
    kind: 'single',
    label: 'Is the company listed in India?',
    helpText: 'BRSR filing obligations attach to specified listed entities. Unlisted companies are not treated as mandatory BRSR filers.',
    required: true,
    indicatorClass: 'profile',
    options: [
      { value: 'listed', label: 'Listed' },
      { value: 'unlisted', label: 'Unlisted' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'exchange',
    stage: 'profile',
    section: 'Listing',
    kind: 'single',
    label: 'Which exchange listing is relevant?',
    required: true,
    indicatorClass: 'profile',
    visibility: listed,
    options: [
      { value: 'nse', label: 'NSE' },
      { value: 'bse', label: 'BSE' },
      { value: 'both', label: 'Both NSE and BSE' },
      { value: 'sme', label: 'SME platform' },
      { value: 'other', label: 'Other' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'cohort',
    stage: 'profile',
    section: 'Listing',
    kind: 'single',
    label: 'Which market-capitalisation range best fits the company for the year you are assessing?',
    helpText:
      'Choose a range rather than typing a market-capitalisation figure. This tool does not check the exchange ranking list.',
    required: true,
    indicatorClass: 'profile',
    visibility: listed,
    options: [
      { value: 'top_150', label: 'Within the top 150' },
      { value: 'top_151_250', label: 'Within the top 250, outside the top 150' },
      { value: 'top_251_500', label: 'Within the top 500, outside the top 250' },
      { value: 'top_501_1000', label: 'Within the top 1,000, outside the top 500' },
      { value: 'outside_top_1000', label: 'Listed, outside the top 1,000' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'cohort_basis',
    stage: 'profile',
    section: 'Listing',
    kind: 'single',
    label: 'How was that range identified?',
    required: true,
    indicatorClass: 'profile',
    visibility: {
      all: [{ questionId: 'listing_status', equals: 'listed' }],
      none: [{ questionId: 'cohort', equals: 'not_sure' }],
    },
    options: [
      { value: 'exchange_list', label: 'Checked against an exchange ranking list' },
      { value: 'internal_estimate', label: 'Internal estimate, not checked against the list' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'financial_year',
    stage: 'profile',
    section: 'Reporting year',
    kind: 'single',
    label: 'Financial year being assessed',
    helpText: 'Indian financial years run from 1 April to 31 March. BRSR Core and value-chain rules change by year.',
    required: true,
    indicatorClass: 'profile',
    options: [
      { value: 'fy2023_24', label: 'FY 2023–24' },
      { value: 'fy2024_25', label: 'FY 2024–25' },
      { value: 'fy2025_26', label: 'FY 2025–26' },
      { value: 'fy2026_27', label: 'FY 2026–27' },
    ],
  },
  {
    id: 'previous_brsr',
    stage: 'profile',
    section: 'Reporting year',
    kind: 'single',
    label: 'Previous BRSR reporting status',
    required: true,
    indicatorClass: 'profile',
    options: [
      { value: 'published', label: 'A BRSR has been published' },
      { value: 'preparing', label: 'Preparing a BRSR, not yet published' },
      { value: 'never', label: 'Has not prepared a BRSR' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'esg_team',
    stage: 'profile',
    section: 'Organisation',
    kind: 'single',
    label: 'Is there an ESG or sustainability team?',
    required: true,
    indicatorClass: 'profile',
    options: [
      { value: 'dedicated', label: 'Yes, a dedicated team' },
      { value: 'shared', label: 'Responsibility sits with another team' },
      { value: 'none', label: 'No named responsibility yet' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'site_count',
    stage: 'profile',
    section: 'Operations',
    kind: 'single',
    label: 'Number of operating sites or facilities',
    required: true,
    indicatorClass: 'profile',
    options: [
      { value: 'one', label: '1' },
      { value: 'two_to_five', label: '2–5' },
      { value: 'six_to_twenty', label: '6–20' },
      { value: 'twenty_plus', label: 'More than 20' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'footprint',
    stage: 'profile',
    section: 'Operations',
    kind: 'single',
    label: 'Where do operations mainly sit?',
    required: true,
    indicatorClass: 'profile',
    options: [
      { value: 'domestic', label: 'Mainly in India' },
      { value: 'international', label: 'Mainly outside India' },
      { value: 'both', label: 'Both India and other countries' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'subsidiaries',
    stage: 'profile',
    section: 'Operations',
    kind: 'single',
    label: 'Does the organisation have subsidiaries or significant group operations?',
    required: true,
    indicatorClass: 'profile',
    options: yesNoUnsure,
  },
  {
    id: 'reporting_boundary_scope',
    stage: 'profile',
    section: 'Operations',
    kind: 'multi',
    label: 'What do you expect the reporting boundary to cover?',
    helpText: 'Select every item that may need to be inside the BRSR boundary. This is not a legal boundary opinion.',
    required: true,
    indicatorClass: 'profile',
    options: [
      { value: 'standalone', label: 'The standalone company' },
      { value: 'subsidiaries', label: 'Subsidiaries' },
      { value: 'associates', label: 'Associates or joint ventures' },
      { value: 'international', label: 'Operations outside India' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'supplies_listed',
    stage: 'profile',
    section: 'Value chain',
    kind: 'single',
    label: 'Does the company supply goods or services to listed companies?',
    required: true,
    indicatorClass: 'profile',
    options: yesNoUnsure,
  },
  {
    id: 'customers_request_esg',
    stage: 'profile',
    section: 'Value chain',
    kind: 'single',
    label: 'Do customers request ESG or emissions information?',
    required: true,
    indicatorClass: 'profile',
    options: yesNoUnsure,
  },
  {
    id: 'prior_external_assessment',
    stage: 'profile',
    section: 'Assurance',
    kind: 'single',
    label: 'Has any ESG disclosure previously gone through external assessment or assurance?',
    helpText: 'This does not assume assurance is required for every metric. Applicability is judged later by year and cohort.',
    required: true,
    indicatorClass: 'profile',
    options: [
      { value: 'assurance', label: 'Yes, external assurance' },
      { value: 'assessment', label: 'Yes, an external assessment' },
      { value: 'no', label: 'No' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },
  {
    id: 'listing_segment',
    stage: 'applicability',
    section: 'Listing check',
    kind: 'single',
    label: 'Which listing segment should the screening use?',
    helpText: 'SME and debt-only listings are not given an automatic top-1,000 conclusion.',
    required: true,
    indicatorClass: 'profile',
    visibility: listed,
    options: [
      { value: 'main_board', label: 'Main-board equity listing' },
      { value: 'sme', label: 'SME platform' },
      { value: 'debt_only', label: 'Debt securities only' },
      { value: 'not_sure', label: 'Not sure' },
    ],
  },

  maturity({
    id: 'disc_boundary',
    stage: 'disclosure',
    section: 'Section A — General disclosures',
    label: 'Can you describe the reporting entity, the reporting boundary and group relationships?',
    helpText: 'Covers identity, holding, subsidiary and associate relationships, and the boundary used for the report.',
    weight: 2,
    naNeedsReview: true,
    principle: 'A',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Section A asks for the reporting boundary and group structure. An unclear boundary makes later indicators hard to interpret.',
    action: 'Write the reporting boundary, list in-scope entities and note any exclusion with a reason.',
  }),
  maturity({
    id: 'disc_operations',
    stage: 'disclosure',
    section: 'Section A — General disclosures',
    label: 'Can you disclose products and services, locations, operations and markets served?',
    helpText: 'Location and market information should match the boundary, including international sites if they are in scope.',
    principle: 'A',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Readers use this to understand where impacts and employees sit.',
    action: 'Compile a site list, main products and markets from a source the business already maintains.',
  }),
  maturity({
    id: 'disc_workforce',
    stage: 'disclosure',
    section: 'Section A — General disclosures',
    label: 'Can you report employees and workers, including the split the BRSR format expects?',
    helpText: 'Think of employees and workers, gender and differently abled persons where the format asks. Do not enter personal records here.',
    weight: 1.5,
    principle: 'A',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Workforce numbers are general disclosures and the base for several later intensity and diversity indicators.',
    action: 'Agree a headcount extract with HR, including workers who are not on the employee payroll, and document the date of the extract.',
  }),
  maturity({
    id: 'disc_csr',
    stage: 'disclosure',
    section: 'Section A — General disclosures',
    label: 'Can you report CSR spending and projects where CSR applies?',
    helpText: 'If CSR under the Companies Act does not apply, choose Not applicable. This tool does not test the statutory CSR thresholds.',
    naNeedsReview: true,
    principle: 'A',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Where CSR applies, the BRSR format asks for CSR information. A wrong “not applicable” answer hides a disclosure.',
    action: 'Confirm with company secretarial whether CSR applies, then map project spend to the disclosure table.',
  }),
  maturity({
    id: 'disc_grievance',
    stage: 'disclosure',
    section: 'Section A — General disclosures',
    label: 'Can you describe grievance and complaint mechanisms and the complaints received?',
    helpText: 'Cover stakeholders such as employees, workers, customers and communities, without naming individuals.',
    principle: 'A',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Complaint volumes and open cases are part of the general disclosures and link to later principles.',
    action: 'Name the channel owner, the log used, and how open and closed complaints are counted for the year.',
  }),
  maturity({
    id: 'disc_material_risks',
    stage: 'disclosure',
    section: 'Section A — General disclosures',
    label: 'Can you disclose material environmental and social risks, and relevant certifications or assessments?',
    principle: 'A',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Material risks explain why particular indicators matter for this company.',
    action: 'List the environmental and social risks already in the risk register and attach current certificates with their scope.',
  }),
  maturity({
    id: 'mgmt_oversight',
    stage: 'disclosure',
    section: 'Section B — Management and process disclosures',
    label: 'Is there board or committee oversight of responsible-business performance?',
    helpText: 'Include who reviews policies and how often the board or a committee sees performance.',
    weight: 2,
    principle: 'B',
    regulatorySourceIds: ['sebi-2021-brsr', 'ngrbc-pib'],
    whyItMatters: 'Section B asks whether governance covers the NGRBC principles, not only whether a policy PDF exists.',
    action: 'Record the committee, its terms of reference and the last date it reviewed sustainability performance.',
  }),
  maturity({
    id: 'mgmt_principles',
    stage: 'disclosure',
    section: 'Section B — Management and process disclosures',
    label: 'Do policies cover the nine NGRBC principles, with an owner for implementation?',
    helpText: 'A single sustainability policy can cover several principles if the coverage is explicit. Gaps should be visible, not implied.',
    weight: 2,
    principle: 'B',
    regulatorySourceIds: ['ngrbc-pib', 'sebi-2021-brsr'],
    whyItMatters: 'Section B is organised around policy coverage of all nine principles.',
    action: 'Map each principle to a policy, an approver and the person accountable for day-to-day implementation.',
  }),
  maturity({
    id: 'mgmt_implementation',
    stage: 'disclosure',
    section: 'Section B — Management and process disclosures',
    label: 'Are policies approved, communicated, monitored and supported by stakeholder engagement and risk review?',
    principle: 'B',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'The format asks about communication, monitoring and whether risks and stakeholder concerns are reviewed.',
    action: 'Document approval, how policies are shared with employees and value-chain partners, and the review cycle.',
  }),
  maturity({
    id: 'mgmt_targets',
    stage: 'disclosure',
    section: 'Section B — Management and process disclosures',
    label: 'Are there performance targets, a management review and a way to correct grievances?',
    principle: 'B',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Targets and corrective action show that policies are managed, not only published.',
    action: 'Set a small set of targets with owners and record how missed targets or upheld grievances are followed up.',
  }),
  maturity({
    id: 'p1_ethics',
    stage: 'disclosure',
    section: 'Section C — Principle 1',
    label: 'Can you report essential indicators on ethics, anti-corruption training, conflicts and disciplinary action?',
    helpText: 'Principle 1: ethics, transparency and accountability. Leadership indicators are not treated as mandatory here.',
    weight: 1.5,
    principle: 'P1',
    regulatorySourceIds: ['ngrbc-pib', 'sebi-2021-brsr'],
    whyItMatters: 'Essential indicators cover training, complaints and actions on integrity. They are separate from a code of conduct that nobody tracks.',
    action: 'Pull training coverage, corruption and conflict complaints, and disciplinary outcomes from the ethics owner for the year.',
  }),
  maturity({
    id: 'p2_products',
    stage: 'disclosure',
    section: 'Section C — Principle 2',
    label: 'Can you report essential indicators on sustainable and safe products, life-cycle thinking and reclaimed materials?',
    helpText: 'Principle 2. Use Not applicable only if the indicator genuinely does not fit the business, and expect a relevance check.',
    naNeedsReview: true,
    principle: 'P2',
    regulatorySourceIds: ['ngrbc-pib', 'sebi-2021-brsr'],
    whyItMatters: 'Product-related R&D, recycled inputs and end-of-life information are essential disclosures for many manufacturers and optional in substance for some service firms.',
    action: 'Identify which Principle 2 indicators apply to your products and collect the percentage or rupee measures from operations.',
  }),
  maturity({
    id: 'p3_wellbeing',
    stage: 'disclosure',
    section: 'Section C — Principle 3',
    label: 'Can you report essential indicators on employee well-being, safety, wages and benefits?',
    helpText: 'Principle 3, including workers as well as employees. Do not paste confidential incident files into this assessment.',
    weight: 1.5,
    principle: 'P3',
    regulatorySourceIds: ['ngrbc-pib', 'sebi-2021-brsr'],
    whyItMatters: 'Well-being spend and safety incidents are also BRSR Core attributes.',
    action: 'Agree definitions for employees and workers, then extract benefits coverage and safety statistics from HR and EHS.',
  }),
  maturity({
    id: 'p4_stakeholders',
    stage: 'disclosure',
    section: 'Section C — Principle 4',
    label: 'Can you identify stakeholders and describe engagement on the topics that affect them?',
    principle: 'P4',
    regulatorySourceIds: ['ngrbc-pib', 'sebi-2021-brsr'],
    whyItMatters: 'Principle 4 asks who the stakeholders are and how the company engages them.',
    action: 'List stakeholder groups, the engagement method and one example of a concern raised and the response.',
  }),
  maturity({
    id: 'p5_human_rights',
    stage: 'disclosure',
    section: 'Section C — Principle 5',
    label: 'Can you report essential human-rights indicators, including training, wages and complaints?',
    weight: 1.5,
    principle: 'P5',
    regulatorySourceIds: ['ngrbc-pib', 'sebi-2021-brsr'],
    whyItMatters: 'Human-rights training, minimum-wage coverage and complaints are essential indicators, and some feed BRSR Core.',
    action: 'Confirm wage-code coverage with HR and record human-rights training and complaint counts without naming people.',
  }),
  maturity({
    id: 'p6_environment',
    stage: 'disclosure',
    section: 'Section C — Principle 6',
    label: 'Can you report essential environmental indicators for energy, emissions, water and waste?',
    helpText: 'This is about disclosure readiness. The next section tests whether the underlying data and evidence exist. Scope 3 is a leadership indicator and is asked separately.',
    weight: 2,
    principle: 'P6',
    regulatorySourceIds: ['ngrbc-pib', 'sebi-2021-brsr', 'sebi-2023-core'],
    whyItMatters: 'Principle 6 essential indicators are the environmental core of BRSR and of BRSR Core.',
    action: 'Map each essential environmental table to a data owner before trying to fill the numbers.',
  }),
  maturity({
    id: 'p6_green_credits',
    stage: 'disclosure',
    section: 'Section C — Principle 6',
    label: 'Can you disclose green credits generated or procured, if you choose to report this leadership indicator?',
    helpText: 'The March 2025 circular introduced this as a voluntary leadership indicator under Principle 6. It is not scored as a mandatory gap.',
    required: false,
    indicatorClass: 'leadership',
    weight: 0.4,
    principle: 'P6',
    regulatorySourceIds: ['sebi-2025-ease'],
    whyItMatters: 'Companies that generate or procure green credits may disclose them. Absence of credits is not itself a filing breach.',
    action: 'If you participate in the green-credit programme, keep the generation or procurement record with the environmental disclosure file.',
  }),
  maturity({
    id: 'p7_advocacy',
    stage: 'disclosure',
    section: 'Section C — Principle 7',
    label: 'Can you disclose public-policy positions and trade-association affiliations where the company advocates?',
    helpText: 'Principle 7. Not applicable can be reasonable if the company does not engage in public-policy advocacy. That choice is reviewed, not scored as success.',
    naNeedsReview: true,
    weight: 0.8,
    principle: 'P7',
    regulatorySourceIds: ['ngrbc-pib', 'sebi-2021-brsr'],
    whyItMatters: 'Where advocacy exists, affiliations and positions are disclosable. A blanket not-applicable answer needs a short explanation.',
    action: 'Ask company secretarial and public affairs for association memberships and any policy positions taken in the year.',
  }),
  maturity({
    id: 'p8_inclusive',
    stage: 'disclosure',
    section: 'Section C — Principle 8',
    label: 'Can you report essential indicators on inclusive growth, sourcing from MSMEs and social impact?',
    principle: 'P8',
    regulatorySourceIds: ['ngrbc-pib', 'sebi-2021-brsr', 'sebi-2023-core'],
    whyItMatters: 'Sourcing from within India and from small producers, and job creation in smaller towns, sit in BRSR Core as well as Principle 8.',
    action: 'Classify purchases and wage locations with procurement and payroll so the percentages can be reproduced.',
  }),
  maturity({
    id: 'p9_consumers',
    stage: 'disclosure',
    section: 'Section C — Principle 9',
    label: 'Can you report essential indicators on consumer complaints, product information and data incidents?',
    naNeedsReview: true,
    principle: 'P9',
    regulatorySourceIds: ['ngrbc-pib', 'sebi-2021-brsr'],
    whyItMatters: 'Consumer complaint turnover and cyber or data incidents are essential indicators for businesses that serve consumers or hold customer data.',
    action: 'Define a complaint with customer care, and ask information security for the count of data incidents in the year.',
  }),

  maturity({
    id: 'ghg_scope1',
    stage: 'esg_data',
    section: 'GHG emissions',
    dimension: 'environmental',
    label: 'Scope 1 emissions',
    helpText: 'Direct emissions from owned or controlled sources. Include fuel and relevant fugitive or process emissions if they exist. A single workbook without factors and review is “weakly controlled”.',
    weight: 2,
    principle: 'P6',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Total Scope 1 emissions are a BRSR Core parameter and a Principle 6 essential indicator.',
    action: 'Collect activity data for fuels and refrigerants, record the emission-factor source, and assign an owner for the inventory.',
  }),
  maturity({
    id: 'ghg_scope2',
    stage: 'esg_data',
    section: 'GHG emissions',
    dimension: 'environmental',
    label: 'Scope 2 emissions',
    helpText: 'Indirect emissions from purchased energy. Keep electricity quantity and the factor or supplier certificate together.',
    weight: 2,
    principle: 'P6',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Scope 2 is reported with Scope 1 and is used in intensity ratios, including PPP-adjusted turnover.',
    action: 'Reconcile purchased electricity and other purchased energy to invoices or meter logs and document the calculation.',
  }),
  maturity({
    id: 'ghg_scope3',
    stage: 'esg_data',
    section: 'GHG emissions',
    dimension: 'environmental',
    label: 'Relevant Scope 3 emissions, where you assess them',
    helpText: 'Scope 3 is a leadership indicator in the BRSR format, not an essential indicator. A gap is not treated as a mandatory filing failure. Choose Not applicable only if you have decided Scope 3 is outside the assessment, and expect that choice to be flagged.',
    indicatorClass: 'leadership',
    weight: 0.5,
    naNeedsReview: true,
    principle: 'P6',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Customers and, where relevant, value-chain disclosures still ask for supplier emissions even when Scope 3 is a leadership indicator.',
    action: 'List the Scope 3 categories you will estimate, the activity data, and whether the factor is spend-based or supplier-specific.',
  }),
  maturity({
    id: 'ghg_method',
    stage: 'esg_data',
    section: 'GHG emissions',
    dimension: 'environmental',
    label: 'Organisational boundary, emission factors, base year and review of the inventory',
    helpText: 'Includes assumptions, estimates, electricity and renewable electricity treatment, and whether two periods can be compared.',
    weight: 1.5,
    principle: 'P6',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'BRSR Core points to a documented measurement approach. Numbers without factors or a boundary cannot be assessed.',
    action: 'Write a short methodology: boundary, factors, estimates, base year and who reviews the total before disclosure.',
  }),
  maturity({
    id: 'energy_data',
    stage: 'esg_data',
    section: 'Energy, water and waste',
    dimension: 'environmental',
    label: 'Energy consumption, fuel, renewable energy and energy intensity',
    helpText: 'Include facility-level data where you have more than one site. Intensity needs both energy and the denominator.',
    weight: 1.5,
    principle: 'P6',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Total energy, renewable share and intensity are BRSR Core energy-footprint parameters.',
    action: 'Build an energy log from fuel issues and electricity bills, mark renewable purchases, and store the intensity formula.',
  }),
  maturity({
    id: 'water_data',
    stage: 'esg_data',
    section: 'Energy, water and waste',
    dimension: 'environmental',
    label: 'Water withdrawal, consumption and discharge, with source evidence',
    helpText: 'If you cannot meter consumption, the Core format allows a calculation from input minus output. Say so. Water-stress context is useful where it is relevant, not a substitute for volumes.',
    weight: 1.5,
    naNeedsReview: true,
    principle: 'P6',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Water consumption, intensity and discharge by treatment level are Core parameters.',
    action: 'Identify meters or bills for each source, define consumption, and record discharge destination and treatment.',
  }),
  maturity({
    id: 'waste_data',
    stage: 'esg_data',
    section: 'Energy, water and waste',
    dimension: 'environmental',
    label: 'Waste generated, recovered and disposed, including hazardous and non-hazardous waste where relevant',
    weight: 1.5,
    principle: 'P6',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'BRSR Core asks for waste by legal category, intensity, recovery and disposal method.',
    action: 'Map waste streams to the categories in the Core format and attach recycler or disposal records for the year.',
  }),
  maturity({
    id: 'env_air',
    stage: 'esg_data',
    section: 'Energy, water and waste',
    dimension: 'environmental',
    label: 'Air emissions and other applicable environmental indicators',
    helpText: 'Principle 6 essential indicators include air emissions such as NOx, SOx and particulate matter where the business has them. Not applicable should be checked if you have stacks, diesel generators or process vents.',
    naNeedsReview: true,
    principle: 'P6',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Air-emission indicators are part of the environmental disclosure even though they are not a separate Core attribute in the July 2023 annexure summary.',
    action: 'Confirm with EHS which air parameters apply and file the monitoring reports against the reporting year.',
  }),
  maturity({
    id: 'env_controls',
    stage: 'esg_data',
    section: 'Environmental controls',
    dimension: 'environmental',
    label: 'Owners, collection frequency, source documents, validation and period-to-period consistency for environmental metrics',
    helpText: 'Choose the top score only if each material metric has an owner, a source, a method, a review and a way to see a break versus last year.',
    weight: 1.5,
    principle: 'P6',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Assessment or assurance tests the trail behind the number, not only the number.',
    action: 'Name a metric owner for energy, emissions, water and waste, and set the collection calendar.',
  }),
  maturity({
    id: 'soc_workforce',
    stage: 'esg_data',
    section: 'Social and governance data',
    dimension: 'social_governance',
    label: 'Employee and worker data, including diversity information you are prepared to disclose',
    helpText: 'An owner, a defined HR extract, a method, records and a review. Do not upload employee-level files.',
    weight: 1.5,
    principle: 'P3',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Workforce and diversity figures support Section A and several principle disclosures.',
    action: 'Freeze a reporting-date extract and document which worker categories are included.',
  }),
  maturity({
    id: 'soc_diversity_safety',
    stage: 'esg_data',
    section: 'Social and governance data',
    dimension: 'social_governance',
    label: 'Health, safety and well-being data for employees and workers',
    helpText: 'Includes benefits coverage and safety incidents. Contract workers on your sites count where the format includes them.',
    weight: 1.5,
    principle: 'P3',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Well-being spend and safety incident rates are BRSR Core parameters.',
    action: 'Define lost-time injury and working hours with EHS, and tie well-being cost to finance.',
  }),
  maturity({
    id: 'soc_training',
    stage: 'esg_data',
    section: 'Social and governance data',
    dimension: 'social_governance',
    label: 'Training and skill-development records',
    principle: 'P3',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Training hours and coverage are essential people indicators and are often split by employee category.',
    action: 'Export training hours from the LMS or attendance sheets and document who is excluded.',
  }),
  maturity({
    id: 'soc_human_rights',
    stage: 'esg_data',
    section: 'Social and governance data',
    dimension: 'social_governance',
    label: 'Human-rights due diligence, wages and related complaints data',
    weight: 1.5,
    principle: 'P5',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Wage ratios and POSH complaints are Core parameters. The assessment does not ask for complaint narratives.',
    action: 'Agree complaint definitions with legal or HR and keep a count log with status, not case files, in the reporting pack.',
  }),
  maturity({
    id: 'soc_anticorruption',
    stage: 'esg_data',
    section: 'Social and governance data',
    dimension: 'social_governance',
    label: 'Anti-corruption and ethical-conduct data',
    principle: 'P1',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Training coverage and action taken on corruption cases are essential Principle 1 indicators.',
    action: 'Reconcile the training register to headcount and record the count of cases and outcomes.',
  }),
  maturity({
    id: 'soc_consumers',
    stage: 'esg_data',
    section: 'Social and governance data',
    dimension: 'social_governance',
    label: 'Consumer complaints, data privacy and consumer-protection records',
    naNeedsReview: true,
    principle: 'P9',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Customer data-breach indicators are part of BRSR Core. Consumer complaint turnaround is a Principle 9 essential indicator.',
    action: 'Define a reportable data incident with information security and a consumer complaint with customer operations.',
  }),
  maturity({
    id: 'soc_community',
    stage: 'esg_data',
    section: 'Social and governance data',
    dimension: 'social_governance',
    label: 'Community and social-impact information, where you undertake it',
    helpText: 'Includes CSR project information where CSR applies, and other community programmes you intend to describe.',
    naNeedsReview: true,
    principle: 'P8',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Social-impact claims need a beneficiary definition and a record, not only a narrative.',
    action: 'List programmes, spend and the evidence you would show for reach or outcome.',
  }),
  maturity({
    id: 'gov_board',
    stage: 'esg_data',
    section: 'Social and governance data',
    dimension: 'social_governance',
    label: 'Board oversight records for sustainability and ethics',
    helpText: 'Minutes or a written report-back are enough. Do not paste confidential board papers into this tool.',
    principle: 'B',
    regulatorySourceIds: ['sebi-2021-brsr'],
    whyItMatters: 'Section B asks whether the board or a committee actually oversees the principles.',
    action: 'File the agenda item and the decision or action log for the last review.',
  }),

  maturity({
    id: 'ev_traceability',
    stage: 'evidence',
    section: 'Evidence and internal controls',
    dimension: 'evidence',
    label: 'Can each reported metric be traced to a source record?',
    helpText: 'The value in the disclosure should point back to a system extract, invoice, meter log or register.',
    weight: 2,
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Assessment or assurance starts from the trail between the disclosed figure and the underlying record.',
    action: 'For each Core metric, name the source system and the report or file that produces the number.',
  }),
  maturity({
    id: 'ev_methodology',
    stage: 'evidence',
    section: 'Evidence and internal controls',
    dimension: 'evidence',
    label: 'Are calculations, assumptions and estimates documented?',
    weight: 1.5,
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Estimated data can be disclosed if the method is clear. Undocumented estimates are a control weakness.',
    action: 'Add a methodology note for every metric that uses a factor, an allocation or an estimate.',
  }),
  maturity({
    id: 'ev_consistency',
    stage: 'evidence',
    section: 'Evidence and internal controls',
    dimension: 'evidence',
    label: 'Are figures consistent across the reporting period and reconciled between departments?',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Energy, finance and HR versions of the same metric often differ. Assurance will ask which one is used.',
    action: 'Run a reconciliation between the disclosure file and the source department before sign-off.',
  }),
  maturity({
    id: 'ev_ownership',
    stage: 'evidence',
    section: 'Evidence and internal controls',
    dimension: 'evidence',
    label: 'Does each material metric have an owner and an approval record?',
    weight: 1.5,
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Ownership and approval are the difference between a collected number and a controlled number.',
    action: 'Record the preparer and the reviewer for each metric family, with the date of approval.',
  }),
  maturity({
    id: 'ev_corrections',
    stage: 'evidence',
    section: 'Evidence and internal controls',
    dimension: 'evidence',
    label: 'Is there a correction process, anomaly check and evidence-retention rule?',
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'Restatements and missing months need a documented path. Retention matters if an assessment happens after the year end.',
    action: 'Write how errors are logged, who approves a change, and how long source evidence is kept.',
  }),
  maturity({
    id: 'ev_reproduction',
    stage: 'evidence',
    section: 'Evidence and internal controls',
    dimension: 'evidence',
    label: 'Could you reproduce a previously reported figure from the retained evidence?',
    weight: 1.5,
    regulatorySourceIds: ['sebi-2023-core'],
    whyItMatters: 'If last year’s number cannot be rebuilt, this year’s comparison and any assessment will stall.',
    action: 'Pick two published or draft metrics and try to rebuild them from the archive. Record what was missing.',
  }),
  maturity({
    id: 'core_environment',
    stage: 'evidence',
    section: 'BRSR Core',
    dimension: 'brsr_core',
    label: 'BRSR Core environmental attributes: GHG, water, energy and waste',
    helpText:
      'Covers data, calculation method, source evidence, owner and review for the Core environmental parameters. Missing data, missing evidence and weak review are different gaps — use the scale to show how far you are.',
    weight: 2,
    visibility: coreVisibility,
    principle: 'P6',
    regulatorySourceIds: ['sebi-2023-core', 'sebi-2025-ease'],
    whyItMatters: 'These attributes are the environmental subset of BRSR Core. Disclosure in the updated format and assessment or assurance follow different year rules, which the applicability screen separates.',
    action: 'Build a Core environmental index: parameter, owner, source, method, evidence location and review status.',
  }),
  maturity({
    id: 'core_social',
    stage: 'evidence',
    section: 'BRSR Core',
    dimension: 'brsr_core',
    label: 'BRSR Core social attributes: well-being and safety, gender, and inclusive development',
    helpText: 'Includes well-being spend, safety incidents, wages paid to women, POSH complaints, MSME or India sourcing, and wages in smaller towns.',
    weight: 2,
    visibility: coreVisibility,
    principle: 'P3',
    regulatorySourceIds: ['sebi-2023-core', 'sebi-2025-ease'],
    whyItMatters: 'These parameters need HR, EHS, legal and procurement data that is defined the same way as the Core format.',
    action: 'Lock definitions for employees, workers, wages, smaller towns and MSME sourcing with the data owners.',
  }),
  maturity({
    id: 'core_governance',
    stage: 'evidence',
    section: 'BRSR Core',
    dimension: 'brsr_core',
    label: 'BRSR Core governance attributes: customer data incidents, accounts payable and openness of business',
    helpText: 'Includes concentration of purchases and sales, and related-party loans, advances and investments. Use finance and secretarial sources.',
    weight: 1.5,
    visibility: coreVisibility,
    principle: 'P1',
    regulatorySourceIds: ['sebi-2023-core', 'sebi-2025-ease'],
    whyItMatters: 'Open-ness of business and payable days come from financial statements and related-party records, which need the same numbers as the audited accounts.',
    action: 'Ask finance to map each openness and payable parameter to the audited figure or working paper.',
  }),
  maturity({
    id: 'core_assessment',
    stage: 'evidence',
    section: 'BRSR Core',
    dimension: 'brsr_core',
    label: 'Readiness for independent assessment or assurance of the Core indicators that apply to you',
    helpText: 'Only some cohorts and years must obtain assessment or assurance. If your screening says it is not yet required, answer for voluntary readiness or choose Not applicable and it will be checked against the year rule.',
    weight: 1.5,
    naNeedsReview: true,
    visibility: coreVisibility,
    regulatorySourceIds: ['sebi-2025-ease'],
    whyItMatters: 'Where the glide path applies, the company must undertake assessment or assurance. The March 2025 circular allows assessment under ISF standards or assurance. This tool does not decide which engagement you need.',
    action: 'If the glide path applies, appoint a preparer for the evidence file and confirm the assessment or assurance scope with the board.',
  }),
  maturity({
    id: 'vc_customer_requirements',
    stage: 'evidence',
    section: 'Value chain',
    dimension: 'value_chain',
    label: 'Are customer ESG or emissions requests documented?',
    helpText: 'This is separate from whether SEBI requires you to file value-chain disclosures. A customer questionnaire can matter even when filing does not.',
    visibility: valueChainVisibility,
    regulatorySourceIds: ['sebi-2025-ease'],
    whyItMatters: 'Undocumented customer requests lead to one-off spreadsheets that cannot be reused.',
    action: 'Keep a register of customer requests: who asked, which metrics, which year and who responds.',
  }),
  maturity({
    id: 'vc_supplier_collection',
    stage: 'evidence',
    section: 'Value chain',
    dimension: 'value_chain',
    label: 'Do you collect environmental and social data from suppliers on a repeatable basis?',
    visibility: valueChainVisibility,
    regulatorySourceIds: ['sebi-2025-ease'],
    whyItMatters: 'Value-chain disclosures, where a company chooses to make them, depend on supplier data. So do many customer requests.',
    action: 'Select the supplier set you will ask, the questions, and the cycle. Do not assume a full value chain on day one.',
  }),
  maturity({
    id: 'vc_coverage',
    stage: 'evidence',
    section: 'Value chain',
    dimension: 'value_chain',
    label: 'Can you measure what share of purchases or sales the collected data covers?',
    helpText: 'Coverage is a management fact. This tool does not apply a numeric SEBI partner threshold because that revised definition was not fully verified in the current configuration.',
    visibility: valueChainVisibility,
    regulatorySourceIds: ['sebi-2025-ease'],
    whyItMatters: 'A percentage of suppliers who replied is not the same as a percentage of purchase or sales value.',
    action: 'Calculate coverage by purchase value and by sales value, and store the supplier list used.',
  }),
  maturity({
    id: 'vc_supplier_evidence',
    stage: 'evidence',
    section: 'Value chain',
    dimension: 'value_chain',
    label: 'Can suppliers support their figures, and can emissions be traced to a source or a calculation?',
    visibility: valueChainVisibility,
    regulatorySourceIds: ['sebi-2025-ease'],
    whyItMatters: 'A supplier number without a method is hard to use in your own disclosure or in a later assessment.',
    action: 'Ask suppliers for the activity data or the factor behind emissions, and record when the figure is only an estimate.',
  }),
  maturity({
    id: 'vc_procurement',
    stage: 'evidence',
    section: 'Value chain',
    dimension: 'value_chain',
    label: 'Does procurement have a repeatable way to collect and review supplier ESG information?',
    visibility: valueChainVisibility,
    regulatorySourceIds: ['sebi-2025-ease'],
    whyItMatters: 'Value-chain readiness fails when it depends on one person emailing spreadsheets before a deadline.',
    action: 'Give procurement a calendar, a completeness check and a reviewer before data is sent to a customer or into a disclosure.',
  }),
  {
    id: 'na_explanation',
    stage: 'evidence',
    section: 'Review',
    kind: 'text',
    label: 'If you marked any item not applicable, briefly say why (optional)',
    helpText: 'This note is printed in the report. It does not remove a relevance check.',
    required: false,
    indicatorClass: 'context',
  },
]

const questionIndex = new Map(BRSR_QUESTIONS.map((question) => [question.id, question]))

export function questionById(id: string): BrsrQuestion | undefined {
  return questionIndex.get(id)
}

export function questionsForStage(stage: BrsrQuestion['stage']): BrsrQuestion[] {
  return BRSR_QUESTIONS.filter((question) => question.stage === stage)
}
